// src/MainWebsite/pages/index/Index.jsx

import React, { Suspense, lazy } from "react";
import axios from "axios";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from "@tanstack/react-query";

import "./Index.css";
import Loading from "../../Components/Loading/Loading";
import FirstSection from "../../Components/HomePage/FirstSection/FirstSection";
import ListContentSlider from "../../Components/HomePage/ListContentSlider/ListContentSlider";
import HomeAccordion from "../../Components/HomePage/Accordion/HomeAccordion";
import HomeClients from "../../Components/HomePage/HomeClients/HomeClients";
import NumbersSection from "../../Components/HomePage/NumbersSection/NumbersSection";
import HomeFooter from "../../Components/HomePage/HomeFooter/HomeFooter";

// Lazy‑load the HomeReels component
const HomeReels = lazy(() =>
  import("../../Components/HomePage/HomeReels/HomeReels")
);

// Create a QueryClient with your desired defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false, // disable auto‑refetch on window focus
    },
  },
});

// Fetch function that sorts your arrays before returning
async function fetchHome() {
  const res = await axios.get(
    `${process.env.REACT_APP_BACKEND_URL}api/HomePages`
  );
  if (!res.data?.length) {
    throw new Error("No homepage data returned");
  }
  const home = res.data[0];

  home.clients.sort((a, b) => a.sortOrder - b.sortOrder);
  home.homePageListContents.sort((a, b) => a.sortOrder - b.sortOrder);
  home.homePageVideos.sort((a, b) => a.sortOrder - b.sortOrder);

  return home;
}

// Inner component that uses useQuery with the v5 single-object API
function IndexContent() {
  const {
    data: home,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["homePage"],
    queryFn: fetchHome,
  });

  if (isLoading) return <Loading />;
  if (isError) return <p className="error">Error: {error.message}</p>;

  return (
    <div className="home-container">
      {home.homePageListContents.length > 0 && (
        <>
          <FirstSection />
          <ListContentSlider listContent={home.homePageListContents} />
        </>
      )}

      <HomeAccordion />

      <div className="video-section-title">
        <p>SOME</p>
        <p>OF</p>
        <p>OUR</p>
        <p>WORK</p>
      </div>
      <div className="mobilevideo-sectiontitle">
        <p>SOME OF</p>
        <p>OUR WORK</p>
      </div>

      <Suspense fallback={<div style={{ height: 300 }}>Loading videos…</div>}>
        <HomeReels reels={home.homePageVideos} />
      </Suspense>

      <HomeClients clients={home.clients} />
      <NumbersSection
        machineSupply={home.medicalMachineSupplied}
        clinics={home.hospitalsClinicsEquipped}
        experience={home.yearsOfExperience}
        support={home.customerSupport}
      />
      <HomeFooter />
    </div>
  );
}

// Wrap the content in QueryClientProvider in this same file
export default function Index() {
  return (
    <QueryClientProvider client={queryClient}>
      <IndexContent />
    </QueryClientProvider>
  );
}
